import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { EventCard } from "@/components/EventCard";
import { HeroVideo, mediaSrc } from "@/components/HeroVideo";
import { ArtistCard } from "@/pages/ArtistsPage";
import { Mountain, Music, Briefcase, Handshake, Store, Mic2 } from "lucide-react";

export const LANE_ICONS = { "trail-rides": Mountain, concerts: Music, corporate: Briefcase, sponsorships: Handshake, vendors: Store, talent: Mic2 };
const FALLBACK = {
  "trail-rides": "https://images.unsplash.com/photo-1533227268428-f9ed0900fb3b?w=1600&q=80&fm=jpg",
  concerts: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600&q=80&fm=jpg",
  corporate: "https://images.unsplash.com/photo-1511578314322-379afb476865?w=1600&q=80&fm=jpg",
  sponsorships: "https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1600&q=80&fm=jpg",
  vendors: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1600&q=80&fm=jpg",
  talent: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=1600&q=80&fm=jpg",
};
const fmtK = (n) => (n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${n}`);

const Statement = ({ kicker, title, body, cta, to, light, testId }) => (
  <section className={`${light ? "section-light" : "bg-black text-white"} px-6 py-28 text-center md:py-36`} data-testid={testId}>
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mx-auto max-w-3xl">
      {kicker && <p className={`eyebrow ${light ? "!text-black" : ""}`}>{kicker}</p>}
      <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl">{title}</h2>
      <p className={`mx-auto mt-7 max-w-2xl text-base leading-relaxed sm:text-lg ${light ? "text-neutral-700" : "text-neutral-300"}`}>{body}</p>
      {cta && <Link to={to} className="btn-chrome mt-10 inline-block px-8 py-3.5 text-[12px]">{cta}</Link>}
    </motion.div>
  </section>
);

export default function HomePage() {
  const [featured, setFeatured] = useState(null);
  const [events, setEvents] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [slogan, setSlogan] = useState("");
  const [media, setMedia] = useState([]);
  const [artists, setArtists] = useState([]);
  const [c, setC] = useState(null);

  useEffect(() => {
    let alive = true;
    const r = (u, set, a = 0) => api.get(u).then((x) => { if (alive) set(x.data); }).catch(() => { if (alive && a < 3) setTimeout(() => r(u, set, a + 1), 700); });
    r("/events/featured", setFeatured); r("/events", setEvents);
    r("/lanes", (d) => { setLanes(d.lanes); setSlogan(d.slogan); });
    r("/media", setMedia); r("/artists", setArtists); r("/site/content", setC);
    return () => { alive = false; };
  }, []);

  const upcoming = events.filter((e) => new Date(e.date).getTime() >= Date.now());
  const laneImg = (id) => mediaSrc(media.find((m) => m.kind === "image" && m.category === id)?.url) || FALLBACK[id];
  if (!c) return <div className="min-h-screen bg-black" />;
  const tagline = c.slogan || slogan;
  const stats = [[c.stat_1_value, c.stat_1_label], [c.stat_2_value, c.stat_2_label], [c.stat_3_value, c.stat_3_label], [c.stat_4_value, c.stat_4_label]];

  return (
    <div className="bg-black text-white">
      {c.announcement && <div className="border-b border-white/10 bg-white px-4 py-2 text-center font-cond text-xs font-bold uppercase tracking-[0.2em] text-black" data-testid="announcement-bar">{c.announcement}</div>}
      {/* HERO */}
      <section className="relative flex min-h-[calc(100vh-5rem)] items-center justify-center overflow-hidden">
        <div className="absolute inset-0 mono-img opacity-60"><HeroVideo fallbackImage={featured?.hero_image || FALLBACK.concerts} /></div>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 px-6 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="eyebrow" data-testid="hero-kicker">{c.hero_kicker}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.8 }}
            className="mt-6 font-display text-5xl leading-[0.95] sm:text-7xl lg:text-[7.5rem]" data-testid="hero-title">
            {c.hero_title_1}<br />{c.hero_title_2}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mx-auto mt-8 max-w-xl text-neutral-300 sm:text-lg" data-testid="hero-slogan">{tagline}</motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/request" data-testid="hero-request-btn" className="btn-gold px-9 py-4 text-[12px]">{c.hero_cta_primary}</Link>
            <Link to="/sponsorships" data-testid="hero-sponsor-btn" className="btn-chrome px-9 py-4 text-[12px]">{c.hero_cta_secondary}</Link>
          </motion.div>
        </div>
        <a href="#about" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-center text-neutral-400" aria-label="Enter">
          <span className="eyebrow block text-[10px]">Enter</span><ArrowDown className="mx-auto mt-2 h-5 w-5 animate-bounce" />
        </a>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-white/10 bg-black py-5" data-testid="marquee">
        <div className="marquee flex whitespace-nowrap font-display text-2xl text-neutral-300 sm:text-3xl">
          {[0, 1].map((k) => (
            <span key={k} className="flex shrink-0">
              {c.marquee.split(",").map((w) => w.trim()).filter(Boolean).map((w) => (
                <span key={w} className="px-8">{w} <span className="px-6 text-neutral-600">·</span></span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="bg-black px-6 py-20" data-testid="stats-section">
        <div className="mx-auto grid max-w-[1200px] grid-cols-2 gap-10 text-center md:grid-cols-4">
          {stats.map(([v, l], i) => (
            <motion.div key={l} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className="font-display text-5xl sm:text-6xl">{v}</div>
              <div className="eyebrow mt-3 text-[10px] text-neutral-400">{l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <div id="about" />
      <Statement testId="about-section" kicker={c.about_kicker} title={c.about_title} body={c.about_body} cta="Find Out More" to="/services" />

      <Statement light testId="biz-section" kicker={c.biz_kicker} title={c.biz_title} body={c.biz_body} cta="Compare The Lanes" to="/services" />

      {/* LANE TILES */}
      <section className="bg-white px-5 pb-28 sm:px-8" data-testid="lanes-section">
        <div className="mx-auto grid max-w-[1400px] gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {lanes.map((l, i) => (
            <motion.div key={l.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
              <Link to={l.link || `/request?lane=${l.id}`} data-testid={`lane-tile-${l.id}`} className="group relative block aspect-[4/3] overflow-hidden bg-black">
                <img src={laneImg(l.id)} alt="" className="mono-img absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                  <h3 className="font-display text-3xl text-white sm:text-4xl">{l.name.split(" & ")[0].split(" Packages")[0]}</h3>
                  <p className="eyebrow mt-3 text-[10px]">from {fmtK(l.starting)} · {l.unit}</p>
                  <span className="mt-5 border-b border-white pb-0.5 font-cond text-[11px] uppercase tracking-[0.2em] text-white opacity-0 transition-opacity group-hover:opacity-100">Find Out More</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SPLIT: REQUEST */}
      <section className="bg-black" data-testid="how-section">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-5 py-24 sm:px-8 md:grid-cols-2">
          <img src={FALLBACK.corporate} alt="" className="mono-img aspect-[4/3] w-full object-cover" />
          <div>
            <p className="eyebrow">How it works</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl">Brief. Scope. Produce.</h2>
            <div className="mt-8 space-y-6">
              {[["01", "Brief", "Three minutes on the request page — lane, date, guests, budget, what success looks like."],
                ["02", "Scope", "A producer returns a line-item proposal within one business day."],
                ["03", "Produce", "Deposit locks the date. E.O.N. runs the show end-to-end."]].map(([n, t, d]) => (
                <div key={n} className="flex gap-5">
                  <span className="font-display text-2xl text-neutral-500">{n}</span>
                  <div><h3 className="font-cond text-xl uppercase tracking-wide">{t}</h3><p className="mt-1 text-neutral-400">{d}</p></div>
                </div>
              ))}
            </div>
            <Link to="/request" data-testid="brief-continue-btn" className="btn-chrome mt-10 inline-block px-8 py-3.5 text-[12px]">Start Your Request</Link>
          </div>
        </div>
      </section>

      <Statement light testId="products-section" kicker={c.products_kicker} title={c.products_title} body={c.products_body} cta="Services & Products" to="/products" />

      {/* EVENTS */}
      <section className="section-light px-5 py-24 sm:px-8" data-testid="events-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="text-center"><p className="eyebrow !text-black">On sale</p><h2 className="mt-4 font-display text-4xl sm:text-5xl">Upcoming Productions</h2></div>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {upcoming.map((e, i) => <EventCard key={e.slug} event={e} index={i} featured={i === 0} />)}
            {!upcoming.length && (
              <div className="py-6 text-center md:col-span-12" data-testid="events-empty">
                <p className="mx-auto max-w-lg text-neutral-700">The season is being announced. Public ticketed productions appear here first — join the list in the footer, or brief us and we'll build the event around you.</p>
                <Link to="/request" className="btn-chrome mt-8 inline-block px-8 py-3.5 text-[12px]">Request an Event</Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ARTISTS */}
      <section className="bg-black px-5 py-24 sm:px-8" data-testid="artists-section">
        <div className="mx-auto max-w-[1400px]">
          <div className="text-center"><p className="eyebrow">Talent booking</p><h2 className="mt-4 font-display text-4xl sm:text-5xl">{c.artists_title}</h2><p className="mx-auto mt-5 max-w-2xl text-neutral-400">{c.artists_body}</p></div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {artists.filter((a) => a.tier === "headliner").map((a, i) => <ArtistCard key={a.id} a={a} i={i} />)}
          </div>
          <div className="mt-10 text-center"><Link to="/artists" data-testid="home-artists-link" className="btn-chrome inline-block px-8 py-3.5 text-[12px]">All Artists</Link></div>
        </div>
      </section>

      <Statement testId="slogan-section" title={tagline} body="" cta="Start Your Request" to="/request" />
      <p className="sr-only" data-testid="slogan-strip">{tagline}</p>
    </div>
  );
}
